"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFuturesReverseParams = void 0;
const react_1 = require("react");
const trade_utils_lib_1 = require("trade_utils_lib");
const interface_1 = require("../../../interface");
const CustomAssetsProvider_1 = require("../../Assets/CustomAssetsProvider");
const PositionModeProvider_1 = require("../../Assets/PositionModeProvider");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const errors_1 = require("../../../interface/errors");
const FutureAPI_1 = require("../../../apis/FutureAPI");
const FutureIndexProvider_1 = require("../../FuturesConfig/FutureIndexProvider");
const TradeValidate_1 = require("./TradeValidate");
const CalculateUtils_1 = require("./CalculateUtils");
const const_1 = require("./../../../component/const");
const calculateReverseQty = (qty, position) => {
    const positionABS = new trade_utils_lib_1.SafeDecimal(position.positionQuantity).abs();
    const maxQty = positionABS.mul(2);
    const minQty = positionABS;
    if (qty.greaterThan(maxQty)) {
        return maxQty;
    }
    else if (qty.lessThanOrEqualTo(0)) {
        return minQty;
    }
    else {
        return qty;
    }
};
const checkTradeError = (qty, symbolInfo, curPrice) => {
    var _a;
    if (qty.lte(0)) {
        return errors_1.TRADE_NO_AVAILABLE;
    }
    // 投 base需要校验最小下单量
    const [minDump, maxQty] = TradeValidate_1.TradeValidator.getMarketQtyRange(symbolInfo, curPrice);
    if (qty.lt(minDump)) {
        const err = errors_1.REVERSE_QTY_LOW_THAN_MIN;
        err.data.min = minDump.toDP((_a = symbolInfo.amount) !== null && _a !== void 0 ? _a : 0, trade_utils_lib_1.SafeDecimal.ROUND_CEIL).toString();
        return err;
    }
    // 超过最大下单量
    if (qty.gt(maxQty)) {
        return errors_1.TRADE_QTY_HIGH_THAN_MAX;
    }
    // TODO 按照下单逻辑进行校验
    return undefined;
};
const useFuturesReverseParams = (props) => {
    const { isFocused } = props;
    const balanceMap = (0, CustomAssetsProvider_1.useCustomAssets)(trade_utils_lib_1.AccountTradeType.trader, isFocused).balanceMap;
    // 判断是否是双向持仓
    const isOpenClose = (0, PositionModeProvider_1.useCheckIsOpenCloseMode)(trade_utils_lib_1.AccountTradeType.trader, isFocused);
    const queryReverseAction = (0, react_1.useCallback)((position, latestPrice) => {
        const symbolInfo = (0, trade_utils_lib_1.getTradeDependencyInstance)().getSymbolInfo(position.symbol);
        if (!symbolInfo) {
            return (0, rxjs_1.throwError)(errors_1.TRADE_SYMBOL_INFO_NONE);
        }
        const balance = balanceMap.get(symbolInfo.quote);
        if (!balance) {
            return (0, rxjs_1.throwError)(errors_1.TRADE_NO_AVAILABLE);
        }
        const originQty = new trade_utils_lib_1.SafeDecimal(position === null || position === void 0 ? void 0 : position.positionQuantity);
        const orderSide = originQty.greaterThanOrEqualTo(0) ? interface_1.CustomOrderSide.SELL : interface_1.CustomOrderSide.BUY;
        return (0, FutureIndexProvider_1.queryFuturesIndexOnce)(symbolInfo.symbol).pipe((0, operators_1.mergeMap)((index) => {
            // 获取预估成交价格
            const esTradePrice = TradeValidate_1.TradeValidator.getAvgMarketPrice(symbolInfo, index.mark_price, orderSide);
            const curPrice = trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(esTradePrice, symbolInfo.precision);
            const markPrice = new trade_utils_lib_1.SafeDecimal(index.mark_price);
            const priceDiff = CalculateUtils_1.CalculateUtils.getPriceDiff({
                latestPrice: new trade_utils_lib_1.SafeDecimal(latestPrice),
                markPrice,
                orderSide,
            });
            const maxCanBuy = CalculateUtils_1.CalculateUtils.calcReverseMaxCanBuy({
                markPrice,
                position,
                tradePrice: new trade_utils_lib_1.SafeDecimal(curPrice),
                availableMargin: new trade_utils_lib_1.SafeDecimal(balance.availableMargin),
                reverseQtyAbs: originQty.abs(),
                totalUPNL: new trade_utils_lib_1.SafeDecimal(balance.unrealized_pnl),
                totalInitMargin: new trade_utils_lib_1.SafeDecimal(balance.total_initial_margin),
                priceDiff,
            });
            const qty = calculateReverseQty(maxCanBuy, position);
            const request = [];
            // 除开开平模式之外，逐仓也需要先撤单再下单
            if (isOpenClose || position.isolated_mode !== interface_1.FutureOrderMarginType.CROSS) {
                // 获取可下的反向仓位
                const reverseQty = qty.sub(originQty.abs());
                // 校验反向仓位的合法性
                const error = checkTradeError(reverseQty, symbolInfo, curPrice);
                if (error) {
                    return (0, rxjs_1.throwError)(error);
                }
                // sell long / buy short
                // 先平仓
                request.push({
                    accountType: const_1.FutureAccountType,
                    symbol: position.symbol,
                    type: interface_1.CustomOrderType.MARKET_QTY,
                    side: orderSide,
                    position_side: orderSide === interface_1.CustomOrderSide.BUY ? interface_1.FuturePositionSide.SHORT : interface_1.FuturePositionSide.LONG,
                    amount: trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(originQty.abs(), symbolInfo.amount),
                    confirmed_sensitive: true,
                });
                // 再开仓
                request.push({
                    accountType: const_1.FutureAccountType,
                    symbol: position.symbol,
                    type: interface_1.CustomOrderType.MARKET_QTY,
                    side: orderSide,
                    position_side: !isOpenClose
                        ? interface_1.FuturePositionSide.BOTH
                        : orderSide === interface_1.CustomOrderSide.BUY
                            ? interface_1.FuturePositionSide.LONG
                            : interface_1.FuturePositionSide.SHORT,
                    amount: trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(reverseQty, symbolInfo.amount),
                    confirmed_sensitive: true,
                });
            }
            else {
                if (qty.lte(originQty)) {
                    return (0, rxjs_1.throwError)(Object.assign({}, errors_1.TRADE_NO_AVAILABLE));
                }
                const error = checkTradeError(qty, symbolInfo, curPrice);
                if (error) {
                    return (0, rxjs_1.throwError)(error);
                }
                // 单项持仓
                request.push({
                    accountType: const_1.FutureAccountType,
                    symbol: position.symbol,
                    type: interface_1.CustomOrderType.MARKET_QTY,
                    side: orderSide,
                    amount: trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(qty, symbolInfo.amount),
                    confirmed_sensitive: true,
                });
            }
            return (0, rxjs_1.of)(request);
        }));
    }, [balanceMap, isOpenClose]);
    const queryReverseRequest = (0, react_1.useCallback)((position) => {
        return queryReverseAction(position).pipe((0, operators_1.catchError)((err) => {
            return (0, rxjs_1.throwError)((0, trade_utils_lib_1.getTradeDependencyInstance)().localizeError(err));
        }));
    }, [queryReverseAction]);
    const reverseWithRequest = (0, react_1.useCallback)((req) => {
        const obs = [...req.map(FutureAPI_1.postCustomOrder)];
        let final = obs[0];
        if (obs.length > 1) {
            final = final.pipe((0, operators_1.delay)(300), (0, operators_1.mergeMap)(() => obs[1]));
        }
        return final.pipe((0, operators_1.catchError)((error) => {
            return (0, rxjs_1.throwError)((0, trade_utils_lib_1.getTradeDependencyInstance)().localizeError(error));
        }));
    }, []);
    const reverseAction = (0, react_1.useCallback)((position) => {
        return queryReverseRequest(position).pipe((0, operators_1.mergeMap)((req) => {
            return reverseWithRequest(req);
        }));
    }, [queryReverseRequest, reverseWithRequest]);
    return { reverseAction, queryReverseRequest, reverseWithRequest };
};
exports.useFuturesReverseParams = useFuturesReverseParams;
